/**
 * Analytics Portal V1 — Resource ratings (Phase 15, plan §4.7 + §11 endpoint 8).
 *
 * Renders only what the server's `ratings` block contains. Lifetime figures come from the resource
 * itself and are labelled all-time; period figures are scoped to the selected range and labelled as
 * such, so a lifetime average is never presented as this period's activity. Nothing is averaged or
 * re-derived in the browser, a real zero stays a zero, and no reviewer identity or review content
 * exists in the payload or on screen.
 */
import React from "react";
import { KpiCard } from "./KpiCard";
import { MetricUnavailable } from "./MetricUnavailable";

const STARS = ["5", "4", "3", "2", "1"];

export const RatingsSection = ({ ratings, rangeSuggestion, onUseSupportedRange }) => {
  if (!ratings) return null;

  const distribution = ratings.distribution;
  const rows = distribution && distribution.rows && !Array.isArray(distribution.rows)
    ? distribution.rows : null;
  const total = rows ? STARS.reduce((sum, star) => sum + (rows[star] || 0), 0) : 0;
  const neverRated = ratings.lifetime_count?.available !== false
    && !ratings.lifetime_count?.value;

  return (
    <section className="space-y-3" data-testid="analytics-resource-ratings"
             aria-labelledby="analytics-resource-ratings-heading">
      <div>
        <h2 id="analytics-resource-ratings-heading"
            className="text-base font-[620] text-foreground md:text-lg">Ratings</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Lifetime figures cover this resource's whole history. Period figures cover the selected
          range only. Individual reviewers and review text are never reported.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard metricKey="rating_lifetime_avg" label="Average rating (lifetime)"
                 hint="All-time average across every rating this resource has received."
                 metric={ratings.lifetime_avg} rangeSuggestion={rangeSuggestion}
                 onUseSupportedRange={onUseSupportedRange} />
        <KpiCard metricKey="rating_lifetime_count" label="Ratings (lifetime)"
                 hint="All-time count." metric={ratings.lifetime_count}
                 rangeSuggestion={rangeSuggestion} onUseSupportedRange={onUseSupportedRange} />
        <KpiCard metricKey="rating_period_count" label="Ratings received (selected period)"
                 metric={ratings.period_count} rangeSuggestion={rangeSuggestion}
                 onUseSupportedRange={onUseSupportedRange} />
        <KpiCard metricKey="rating_period_avg" label="Average rating submitted (selected period)"
                 hint="Averaged by the server from the ratings submitted in this period."
                 metric={ratings.period_avg} rangeSuggestion={rangeSuggestion}
                 onUseSupportedRange={onUseSupportedRange} />
      </div>

      {neverRated && (
        <p className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
           role="note" data-testid="analytics-ratings-not-rated">
          This resource has not been rated yet. Ratings will appear here once members rate it.
        </p>
      )}

      {distribution && (
        <div className="rounded-xl border border-border bg-card p-4"
             data-testid="analytics-ratings-distribution">
          <h3 className="text-sm font-[640] text-foreground">Ratings by star level</h3>
          {rows ? (
            <>
              <p className="mt-0.5 text-xs text-muted-foreground"
                 data-testid="analytics-ratings-distribution-summary">
                {total > 0
                  ? `${total.toLocaleString()} rating${total === 1 ? "" : "s"} submitted in the selected period: `
                    + STARS.map((star) => `${star} star ${(rows[star] || 0).toLocaleString()}`).join(", ")
                  : "No ratings were submitted in the selected period."}
              </p>
              <ul className="mt-3 space-y-2">
                {STARS.map((star) => {
                  const count = rows[star] || 0;
                  const pct = total ? Math.round((count / total) * 100) : 0;
                  return (
                    <li key={star} className="flex items-center gap-3"
                        data-testid={`analytics-ratings-star-${star}`}>
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">
                        {star} star
                      </span>
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                        <span className="block h-full rounded-full bg-primary"
                              style={{ width: `${pct}%` }} aria-hidden="true" />
                      </span>
                      <span className="w-10 shrink-0 text-right text-xs font-[620] text-foreground">
                        {count.toLocaleString()}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <div className="mt-2">
              <MetricUnavailable metricKey="rating_distribution" metric={distribution}
                                 rangeSuggestion={rangeSuggestion}
                                 onUseSupportedRange={onUseSupportedRange} />
            </div>
          )}
        </div>
      )}
    </section>
  );
};
